export const BuyerPerspective = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Buyer's View</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            How Buyers Analyze Your T12
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Understanding the buyer's perspective helps you present your T12 in the best possible light and anticipate their questions and concerns.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/455516fadbf575d29b379156fc06b4aa.jpg"
                alt="Real estate investor conducting T12 financial analysis"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">The Buyer's T12 Checklist</h3>
              <p className="text-gray-700 mb-4">
                Sophisticated buyers follow a systematic process to evaluate T12 statements. Here's exactly what they're looking for:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Verify Income Against Rent Roll</div>
                    <p className="text-sm text-gray-700">Calculate: Units × Average Rent × 12 months. Compare to T12 rental income. Any discrepancy over 5% requires explanation.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Cross-Check Bank Statements</div>
                    <p className="text-sm text-gray-700">Match T12 income to actual bank deposits. Every dollar must be accounted for. Missing deposits are immediate red flags.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Compare to Tax Returns</div>
                    <p className="text-sm text-gray-700">Schedule E should align with T12. Major differences suggest either tax fraud or inflated T12 numbers.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">4</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Analyze Expense Ratios</div>
                    <p className="text-sm text-gray-700">Compare your expenses to industry benchmarks. Expenses below 35% or above 50% trigger deeper investigation.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">5</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Identify Trends</div>
                    <p className="text-sm text-gray-700">Look for patterns in income growth, expense increases, and vacancy rates. Negative trends reduce offers significantly.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">6</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Normalize NOI</div>
                    <p className="text-sm text-gray-700">Adjust for one-time items, missing expenses (like management fees), and market-rate assumptions. This becomes their underwriting NOI.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Buyer's T12 Adjustments</h3>
          <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto">
            Buyers rarely accept your T12 NOI at face value. They make adjustments to arrive at their "underwriting NOI" which determines their offer price.
          </p>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4">Your T12 NOI</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Gross Rental Income</span>
                    <span className="font-semibold text-gray-900">$345,600</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Other Income</span>
                    <span className="font-semibold text-gray-900">$15,600</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Vacancy Loss (5%)</span>
                    <span className="font-semibold text-red-600">-$17,280</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border-t-2 border-gray-300">
                    <span className="font-bold text-gray-900">Effective Gross Income</span>
                    <span className="font-bold text-gray-900">$343,920</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Operating Expenses</span>
                    <span className="font-semibold text-gray-900">$160,714</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg border-2 border-teal-200">
                    <span className="font-bold text-gray-900">Your T12 NOI</span>
                    <span className="font-bold text-teal-600 text-xl">$183,206</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-4">Buyer's Adjusted NOI</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Gross Rental Income</span>
                    <span className="font-semibold text-gray-900">$345,600</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Other Income</span>
                    <span className="font-semibold text-gray-900">$15,600</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-gray-700">Vacancy Loss (8% market)</span>
                    <span className="font-semibold text-red-600">-$27,648</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border-t-2 border-gray-300">
                    <span className="font-bold text-gray-900">Effective Gross Income</span>
                    <span className="font-bold text-gray-900">$333,552</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-gray-700">Operating Expenses</span>
                    <span className="font-semibold text-gray-900">$160,714</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-gray-700">Add: Management (8%)</span>
                    <span className="font-semibold text-red-600">+$26,684</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-gray-700">Add: Deferred Maintenance</span>
                    <span className="font-semibold text-red-600">+$15,000</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border-2 border-red-200">
                    <span className="font-bold text-gray-900">Buyer's Underwriting NOI</span>
                    <span className="font-bold text-red-600 text-xl">$131,154</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-6 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-gray-700 mb-2"><strong>Your Expected Value (Your NOI):</strong></div>
                  <div className="text-2xl font-bold text-gray-900">$183,206 ÷ 6.5% = $2,818,554</div>
                </div>
                <div>
                  <div className="text-sm text-gray-700 mb-2"><strong>Buyer's Offer (Adjusted NOI):</strong></div>
                  <div className="text-2xl font-bold text-red-600">$131,154 ÷ 6.5% = $2,017,754</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-red-300">
                <p className="text-gray-700">
                  <strong>Gap: $800,800 (28% lower than expected)</strong> - This is why clean, accurate T12 documentation and addressing issues before listing is critical.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-question-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Questions Buyers Ask</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                <span>Why is vacancy higher than market average?</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                <span>What major repairs are needed in next 2 years?</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                <span>Are rents at, above, or below market?</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                <span>Why are expenses trending upward?</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                <span>What's included in "other income"?</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-3xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Documents They Request</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                <span>12 months of bank statements</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                <span>3 years of tax returns (Schedule E)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                <span>Current rent roll with lease dates</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                <span>Utility bills and invoices</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                <span>Property tax statements</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-thumb-up-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">What Impresses Buyers</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                <span>Clean, professional T12 formatting</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                <span>All supporting docs organized and ready</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                <span>Consistent month-to-month performance</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                <span>Transparent explanations for anomalies</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                <span>Growing income, controlled expenses</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};